<template>
    <div class="flex-level flex-top activity-card">
        <div v-if="oneLog.cardType === 'object'" class="template-card-v2__object">
            <slot name="icon"></slot>
        </div>

        <div
            ref="timelineCard"
            :class="activityCardClass"
            @mouseenter="toggleActionBar(true)"
            @mouseleave="toggleActionBar(false)"
        >
            <!-- CARD -->
            <div class="card-body p-0 wp-10" @click.stop="emitClick">
                <!-- BADGES (privé/ épinglé)-->
                <div class="activity-card__container-badges">
                    <!-- Private badge -->
                    <div
                        v-if="oneLog.isPrivate"
                        class="activity-card__container-badges__badge activity-card__container-badges__badge--private typographies-paragraph-small-bold"
                    >
                        <CompassIcon size="extra-small" icon="lock-fill" />
                        {{ $l10n.__('Privé') }}
                    </div>
                    <!-- Pin badge -->
                    <div
                        v-if="oneLog.pin"
                        class="activity-card__container-badges__badge activity-card__container-badges__badge--pinned typographies-paragraph-small-bold"
                    >
                        <CompassIcon size="extra-small" icon="pin-fill" />
                        {{ $l10n.__('Épinglé') }}
                        <span
                            v-if="registeredUserPin.length > 0"
                            class="activity-card__container-badges__badge--pinned activity-card__container-badges__badge__username"
                        >
                            ({{ registeredUserPin }})
                        </span>
                    </div>
                </div>
                <div class="flex-column ov-hidden">
                    <div class="activity-card-header__container">
                        <!-- ABOVE CARD - TYPE / DATE / AUTHOR -->
                        <slot name="label"></slot>
                        <div
                            class="activity-card-header-action__container flex-item-right ovy-reset flex-row ai-center gray-60 fs-2"
                        >
                            <span
                                v-if="!oneLog.allDay"
                                class="nowrap activity-card-header-date__container mr-2"
                            >
                                {{
                                    $filters.dateFormatter(
                                        oneLog.timestamp,
                                        'NORMAL_DATE_TIME',
                                        true,
                                    )
                                }}
                            </span>
                            <span v-else class="nowrap activity-card-header-date__container mr-2">
                                {{ $filters.dateFormatter(oneLog.timestamp, 'NORMAL_DATE', true) }}
                            </span>
                            <component
                                :is="actionBarComponent"
                                v-show="isActionBarVisible"
                                ref="timelineActionBar"
                                :oneLog="oneLog"
                                :linkedid="linkedid"
                                :linkedtype="linkedtype"
                                :isDocument="isDocument"
                                :overviewType="overviewType"
                                :subtype="getCardType"
                                :isActionBarVisible="isActionBarVisible"
                                :isPinned="isPinned"
                                :pinStatusLabel="pinStatusLabel"
                                :displayHistory="displayHistory"
                                @toggleHistory="toggleHistory"
                                @toggle-pin="pin"
                            />
                        </div>
                    </div>
                    <div>
                        <!-- CONTENT SECTION -->
                        <slot name="content"></slot>
                    </div>
                </div>

                <!-- LINKED SECTION | ATTACHMENT -->
                <slot name="linked">
                    <div v-if="showLinked" class="activity-card-attachment__container">
                        <template v-if="oneLog.contacts && oneLog.contacts.list">
                            <TemplateCardAttachment
                                v-for="contact in oneLog.contacts.list"
                                :key="`${contact.type}-${contact.id}`"
                                :item="contact"
                            />
                        </template>

                        <template v-if="oneLog.linkeds && oneLog.linkeds.length > 0">
                            <TemplateCardAttachment
                                v-for="linked in oneLog.linkeds"
                                :key="`${linked.type}-${linked.id}`"
                                :item="linked"
                            />
                        </template>
                    </div>
                </slot>
                <!-- LOG LIST -->
                <slot name="footer">
                    <div v-if="history" :class="{ 'mt-3': displayHistory }">
                        <HistoryCardLog
                            :histories="history"
                            :displayHistory="displayHistory"
                            :loading="historyLoading"
                            :linkedid="linkedid"
                            :linkedtype="linkedtype"
                            @close-history="toggleHistory"
                        ></HistoryCardLog>
                    </div>
                </slot>
            </div>
        </div>
    </div>
</template>

<script>
import TemplateCardAttachment from 'vcomponents/timeline/TemplateCardAttachment.vue';
import HistoryCardLog from './HistoryCardLog.vue';
import TimelineActionBar from 'vcomponents/timeline/timelineActionBar/TimelineActionBar.vue';
import TimelineActionBarAnnotation from 'vcomponents/timeline/timelineActionBar/TimelineActionBarAnnotation.vue';
import TimelineActionBarTask from 'vcomponents/timeline/timelineActionBar/TimelineActionBarTask.vue';
import TimelineActionBarEmail from 'vcomponents/timeline/timelineActionBar/TimelineActionBarEmail.vue';
import TimelineActionBarEvent from 'vcomponents/timeline/timelineActionBar/TimelineActionBarEvent.vue';
import TimelineActionBarPhoneCall from 'vcomponents/timeline/timelineActionBar/TimelineActionBarPhoneCall.vue';
import TimelineCardMixin from './TimelineCardMixin.js';
import { createNamespacedHelpers } from 'vuex';

const { mapState: mapStateUser } = createNamespacedHelpers('user');

export default {
    components: {
        TimelineActionBar,
        TimelineActionBarAnnotation,
        TimelineActionBarTask,
        TimelineActionBarEmail,
        TimelineActionBarEvent,
        TimelineActionBarPhoneCall,
        HistoryCardLog,
        TemplateCardAttachment,
    },
    mixins: [TimelineCardMixin],
    props: {
        oneLog: {
            type: Object,
            required: true,
        },
        linkedid: {
            type: Number,
            required: true,
        },
        linkedtype: {
            type: String,
            required: true,
        },
        isDanger: {
            type: Boolean,
            required: false,
            default: false,
        },
        isDocument: {
            type: Boolean,
            required: false,
            default: false,
        },
        overviewType: {
            type: String,
            required: false,
            default: '',
        },
    },

    data() {
        return {
            isActionBarVisible: false,
            displayHistory: false,
            history: [],
            historyLoaded: false,
            historyLoading: false,
        };
    },
    computed: {
        ...mapStateUser({
            userForename: 'forename',
            userName: 'name',
        }),
        actionBarComponent() {
            switch (this.oneLog.card) {
                case 'annotation':
                    return 'TimelineActionBarAnnotation';
                case 'task':
                    return 'TimelineActionBarTask';
                case 'email':
                    return 'TimelineActionBarEmail';
                case 'event':
                    return 'TimelineActionBarEvent';
                case 'phonecall':
                    return 'TimelineActionBarPhoneCall';
                default:
                    return 'TimelineActionBar';
            }
        },
        showLinked() {
            return (
                (this.oneLog.staffs &&
                    this.oneLog.staffs.params &&
                    this.oneLog.staffs.params.list.length > 0) ||
                (this.oneLog.linkeds && this.oneLog.linkeds.length > 0) ||
                (this.oneLog.contacts &&
                    this.oneLog.contacts.list &&
                    this.oneLog.contacts.params.list.length > 0)
            );
        },
        activityCardClass() {
            return {
                'b-1-red': this.isDanger && !this.oneLog.isPrivate,
                'b-1-gray-60-dashed': this.oneLog.isPrivate && !this.isDanger,
                'b-1-red-dashed': this.oneLog.isPrivate && this.isDanger,
                'template-card-v2__container': true,
            };
        },
        registeredUserPin() {
            const name =
                this.oneLog.pin?.staff_first_name || this.oneLog.pin?.staff_last_name
                    ? `${this.oneLog.pin.staff_first_name || ''}
                    ${this.oneLog.pin.staff_last_name || ''}`
                    : '';
            return name.trim();
        },
    },
    methods: {
        toggleActionBar(value) {
            if (this.oneLog.mocked) {
                this.isActionBarVisible = false;
                return;
            }
            if (this.$refs.timelineActionBar.$refs.actionBarMenu?.isVisible) {
                //The action bar is visible, event listener added to hide it on click outside of card
                document.addEventListener('click', this.onClickOutside);
                this.isActionBarVisible = true;
                return;
            }
            //The action bar is not visible, event listener removed
            document.removeEventListener('click', this.onClickOutside);
            this.isActionBarVisible = value;
        },
        toggleHistory() {
            this.displayHistory = !this.displayHistory;

            if (this.displayHistory && !this.historyLoaded) {
                this.historyLoading = true;
                const objectid = this.oneLog.historyFilter
                    ? this.oneLog.historyFilter
                    : this.oneLog.objectid;
                window.SellsyJS.WebServiceCaller.get(
                    `/timeline/history/${this.linkedtype}/${this.linkedid}/${this.oneLog.objecttype}/${objectid}`,
                    {},
                    (response) => {
                        this.history = response;
                        this.historyLoaded = true;
                        this.historyLoading = false;
                    },
                );
            }
            this.$emit('toggle-history', this.displayHistory);
        },
        getIcon(type) {
            switch (type) {
                case 'third':
                case 'prospect':
                case 'client':
                case 'supplier':
                    return 'company';
                case 'contact':
                case 'thirdcontact':
                case 'people':
                    return 'contact';
                case 'ticket':
                    return 'helpdesk';
                case 'opportunity':
                    return 'opportunity';
                case 'phonecall':
                    return 'sellsyphone';
                case 'email':
                case 'nylasaccount':
                    return 'email';
                case 'task':
                    return 'task';
                case 'event':
                case 'agendaeventlink':
                    return 'agenda';
                case 'timetracking':
                    return 'timetracking';
                case 'project':
                    return 'project';
                case 'document':
                case 'estimate':
                case 'invoice':
                case 'order':
                case 'delivery':
                case 'proforma':
                case 'creditnote':
                case 'model':
                    return 'folder';
                case 'purchase':
                case 'purinvoice':
                case 'purorder':
                case 'purdelivery':
                case 'purcreditnote':
                    return 'purchase';
                case 'campaign':
                    return 'marketing';
                case 'esign':
                    return 'edit';
                case 'propdocument':
                case 'proptemplate':
                case 'proposal':
                    return 'redactor';
                case 'item':
                case 'service':
                    return 'catalog';
                case 'annotation':
                    return 'conversation';
                case 'tracking':
                    return 'star';
                default:
                    break;
            }
        },
        isDeleted(person) {
            return person.isDeleted || person.isBlocked;
        },
        onClickOutside(event) {
            const card = this.$refs.timelineCard;
            if (card && !card.contains(event.target)) {
                // Clicked outside, hide the action bar
                document.removeEventListener('click', this.onClickOutside);
                this.isActionBarVisible = false;
            }
        },
        emitClick() {
            this.$emit('card-click');
        },
        pin() {
            this.togglePin(this.isPinned, {
                userForename: this.userForename,
                userName: this.userName,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;

.activity-card {
    position: relative;

    &__container-badges {
        position: absolute;
        left: 56px;
        top: -10px;
        margin-bottom: 6px;
        display: flex;
        justify-content: flex-start;
        align-items: flex-start;
        gap: var(--spacing-1);

        &__badge {
            padding: 2px var(--spacing-1);
            border: 1px solid var(--colors-common-border-lighter);
            border-radius: 12px;
            gap: var(--spacing-0);
            display: flex;
            background-color: var(--colors-common-background-default);
            align-items: center;

            &__username {
                font-weight: var(--font-weights-lato-2);
            }

            &--private {
                color: var(--colors-common-text-subtle-default);
            }

            &--pinned {
                color: var(--colors-sellsy-yellow-500);
            }
        }
    }

    &-header {
        &__container {
            display: flex;
            align-items: center;
            margin-bottom: var(--spacing-1);
        }
        &-date__container {
            line-height: 16px;
        }
    }

    &-attachment__container {
        display: flex;
        flex-wrap: wrap;
        color: var(--colors-common-text-subtle-default);
        border-top: 1px solid var(--colors-common-border-lighter);
        padding: var(--spacing-1) 0 0;
        margin: var(--spacing-3) 0 0;
        gap: var(--spacing-3);
    }
}
</style>

<style lang="scss" scoped>
.template-card-v2 {
    &__object {
        &::before {
            content: ' ';
            position: absolute;
            left: 16px;
            top: 36px;
            height: calc(100% - 36px);
            border-left: solid 1px var(--colors-component-border-actionable-default);
        }
        .incoming-events &::before {
            border: dashed 1px var(--colors-component-border-actionable-default);
        }
    }
    &__container {
        width: 100%;
        box-shadow: 0px 2px 2px rgba(86, 86, 101, 0.1);
        background-color: var(--colors-common-background-default);
        border-radius: var(--spacing-0);
        padding: var(--spacing-3);
        margin: 0 0 var(--spacing-2) var(--spacing-3);
    }
}
</style>
